/**
 * LegalTemplates PDF Editor — Unicode fonts for export
 *
 * pdf-lib's built-in fonts (Helvetica, Times, Courier) only encode WinAnsi:
 * any character outside Latin-1 used to be written as "?". For everything
 * else the editor embeds a metric-compatible Liberation face (SIL OFL,
 * shipped with the app): Sans for Helvetica, Serif for Times, Mono for
 * Courier. Liberation covers Latin, Greek and Cyrillic; scripts it lacks
 * (CJK, Arabic, Hebrew, Thai…) are flagged to the person before they save.
 *
 * Font files are Vite assets, fetched once on first use and cached.
 */
import liberationSansRegular from '../../fonts/LiberationSans-Regular.ttf?url';
import liberationSansBold from '../../fonts/LiberationSans-Bold.ttf?url';
import liberationSansItalic from '../../fonts/LiberationSans-Italic.ttf?url';
import liberationSansBoldItalic from '../../fonts/LiberationSans-BoldItalic.ttf?url';
import liberationSerifRegular from '../../fonts/LiberationSerif-Regular.ttf?url';
import liberationSerifBold from '../../fonts/LiberationSerif-Bold.ttf?url';
import liberationSerifItalic from '../../fonts/LiberationSerif-Italic.ttf?url';
import liberationSerifBoldItalic from '../../fonts/LiberationSerif-BoldItalic.ttf?url';
import liberationMonoRegular from '../../fonts/LiberationMono-Regular.ttf?url';
import liberationMonoBold from '../../fonts/LiberationMono-Bold.ttf?url';
import liberationMonoItalic from '../../fonts/LiberationMono-Italic.ttf?url';
import liberationMonoBoldItalic from '../../fonts/LiberationMono-BoldItalic.ttf?url';
import { state } from './context.js';
import { showAlert } from './ui.js';

// pdf-lib StandardFonts name → Liberation asset.
const UNICODE_FONT_URLS = {
    Helvetica: liberationSansRegular,
    HelveticaBold: liberationSansBold,
    HelveticaOblique: liberationSansItalic,
    HelveticaBoldOblique: liberationSansBoldItalic,
    TimesRoman: liberationSerifRegular,
    TimesRomanBold: liberationSerifBold,
    TimesRomanItalic: liberationSerifItalic,
    TimesRomanBoldItalic: liberationSerifBoldItalic,
    Courier: liberationMonoRegular,
    CourierBold: liberationMonoBold,
    CourierOblique: liberationMonoItalic,
    CourierBoldOblique: liberationMonoBoldItalic,
};

const fontBytesCache = new Map();

// WinAnsi (cp1252): ASCII plus the Latin-1 block and a handful of typographic
// characters in 0x80–0x9F. pdf-lib's standard fonts reject anything else.
const WINANSI_EXTRA = new Set('€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ'.split(''));

export function isWinAnsiChar(char) {
    const code = char.codePointAt(0);
    if (code >= 0x20 && code <= 0x7e) {
        return true;
    }
    if (code >= 0xa0 && code <= 0xff) {
        return true;
    }
    if (code === 0x0a || code === 0x0d || code === 0x09) {
        return true;
    }
    return WINANSI_EXTRA.has(char);
}

export function isWinAnsiText(text) {
    for (const char of String(text || '')) {
        if (!isWinAnsiChar(char)) {
            return false;
        }
    }
    return true;
}

export function unicodeFontUrlFor(standardFontName) {
    return UNICODE_FONT_URLS[standardFontName] || UNICODE_FONT_URLS.Helvetica;
}

export async function fetchUnicodeFontBytes(standardFontName) {
    const url = unicodeFontUrlFor(standardFontName);
    if (!fontBytesCache.has(url)) {
        fontBytesCache.set(url, fetch(url).then(async (response) => {
            if (!response.ok) {
                throw new Error(`Could not load font (${response.status}).`);
            }
            return new Uint8Array(await response.arrayBuffer());
        }).catch((error) => {
            fontBytesCache.delete(url);
            throw error;
        }));
    }
    return fontBytesCache.get(url);
}

// True for fonts embedded through fontkit (they expose the parsed font).
export function isUnicodeFont(pdfFont) {
    const inner = pdfFont && pdfFont.embedder && pdfFont.embedder.font;
    return !!(inner && typeof inner.hasGlyphForCodePoint === 'function');
}

export function fontCanRenderChar(pdfFont, char) {
    if (isUnicodeFont(pdfFont)) {
        try {
            return pdfFont.embedder.font.hasGlyphForCodePoint(char.codePointAt(0));
        } catch (error) {
            return false;
        }
    }
    try {
        pdfFont.widthOfTextAtSize(char, 10);
        return true;
    } catch (error) {
        return false;
    }
}

// Scripts the bundled Liberation faces cover. Anything else exports as "?".
const SUPPORTED_SCRIPT = /[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}\p{Script=Common}\p{Script=Inherited}\s]/u;

export function findUnsupportedChars(text) {
    const found = new Set();
    for (const char of String(text || '')) {
        if (!SUPPORTED_SCRIPT.test(char)) {
            found.add(char);
        }
    }
    return Array.from(found);
}

/**
 * Warn — once per new character — when edits contain characters that will
 * not survive export. Called whenever the annotation list changes.
 */
export function noteUnsupportedScripts() {
    if (!state.unsupportedCharsWarned) {
        state.unsupportedCharsWarned = new Set();
    }
    const fresh = [];
    state.annotations.forEach((annotation) => {
        const texts = [];
        if (typeof annotation.text === 'string') {
            texts.push(annotation.text);
        }
        if (Array.isArray(annotation.runs)) {
            annotation.runs.forEach((run) => {
                if (run && typeof run.text === 'string') {
                    texts.push(run.text);
                }
            });
        }
        if (Array.isArray(annotation.lines)) {
            annotation.lines.forEach((line) => {
                (Array.isArray(line && line.runs) ? line.runs : []).forEach((run) => {
                    if (run && typeof run.text === 'string') {
                        texts.push(run.text);
                    }
                });
            });
        }
        texts.forEach((text) => {
            findUnsupportedChars(text).forEach((char) => {
                if (!state.unsupportedCharsWarned.has(char)) {
                    state.unsupportedCharsWarned.add(char);
                    fresh.push(char);
                }
            });
        });
    });
    if (fresh.length) {
        const sample = fresh.slice(0, 6).join(' ');
        showAlert(`Some characters (${sample}) are in a script the editor cannot embed yet. They will show as "?" in the saved file.`, true);
    }
}
